import { useState } from 'react';
import { PUBLISH_PERMISSIONS, type PermissionCode } from '@/constants/permissions';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { BatchActionDialog } from '../components/BatchActionDialog';
import { BatchPanelPage } from '../components/BatchPanelPage';
import type { BatchPanelStage } from '../hooks/useBatchFilters';
import { useConfirmBatch, useDisburseBatch } from '../hooks/usePublishMutations';
import type { Batch } from '../types/publish.types';

type BatchApprovalStage = Exclude<BatchPanelStage, 'sales'>;

interface BatchApprovalStageConfig {
  title: string;
  emptyMessage: string;
  rowActionLabel: string;
  rowActionRequires: PermissionCode;
  canTakeRowAction: (batch: Batch) => boolean;
}

const ARCHIVED_DISBURSE_BLOCKED =
  'The DCRs behind this batch are no longer held, so it cannot be checked before paying';

const BATCH_APPROVAL_STAGE_CONFIG: Record<BatchApprovalStage, BatchApprovalStageConfig> = {
  mis: {
    title: 'MIS Publish Panel',
    emptyMessage: 'No batch has come to MIS yet',
    rowActionLabel: 'Confirm',
    rowActionRequires: PUBLISH_PERMISSIONS.MIS_CONFIRM,
    canTakeRowAction: (batch) => batch.isConfirm !== true,
  },
  accounts: {
    title: 'Accounts Disburse Panel',
    emptyMessage: 'No confirmed batch is waiting to be paid',
    rowActionLabel: 'Disburse',
    rowActionRequires: PUBLISH_PERMISSIONS.ACCOUNTS_DISBURSE,
    canTakeRowAction: (batch) => !batch.isDisburse && !batch.rowsArchived,
  },
};

interface BatchApprovalPageProps {
  stage: BatchApprovalStage;
}

export function BatchApprovalPage({ stage }: BatchApprovalPageProps) {
  const { can } = usePermissions();
  const config = BATCH_APPROVAL_STAGE_CONFIG[stage];

  const [confirming, setConfirming] = useState<Batch | null>(null);
  const [sendingBack, setSendingBack] = useState<Batch | null>(null);
  const [disbursing, setDisbursing] = useState<Batch | null>(null);

  const confirm = useConfirmBatch(() => {
    setConfirming(null);
    setSendingBack(null);
  });
  const disburse = useDisburseBatch(() => setDisbursing(null));

  const openRowActionDialog: Record<BatchApprovalStage, (batch: Batch) => void> = {
    mis: setConfirming,
    accounts: setDisbursing,
  };

  return (
    <BatchPanelPage
      stage={stage}
      title={config.title}
      emptyMessage={config.emptyMessage}
      rowActions={[
        {
          label: config.rowActionLabel,
          onClick: openRowActionDialog[stage],
          when: (batch) => can(config.rowActionRequires) && config.canTakeRowAction(batch),
        },
        ...(stage === 'mis'
          ? [
              {
                label: 'Send back',
                tone: 'destructive' as const,
                onClick: setSendingBack,
                when: (batch: Batch) =>
                  can(PUBLISH_PERMISSIONS.MIS_CONFIRM) && batch.isConfirm === null,
              },
            ]
          : []),
      ]}
    >
      <BatchActionDialog
        batch={confirming}
        title="Confirm this batch?"
        description="MIS signs the batch off and it moves to Accounts."
        confirmLabel="Confirm"
        commentRequired
        isPending={confirm.isPending}
        onConfirm={(comments) => {
          if (confirming !== null) {
            confirm.mutate({
              batchId: confirming.batchId,
              isConfirm: true,
              comments,
            });
          }
        }}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
      />

      <BatchActionDialog
        batch={sendingBack}
        title="Send this batch back?"
        description="Every DCR and expense line in it loses the batch and returns to the publish queue, so it can be cut again."
        confirmLabel="Send back"
        destructive
        commentRequired
        isPending={confirm.isPending}
        onConfirm={(comments) => {
          if (sendingBack !== null) {
            confirm.mutate({
              batchId: sendingBack.batchId,
              isConfirm: false,
              comments,
            });
          }
        }}
        onOpenChange={(open) => {
          if (!open) setSendingBack(null);
        }}
      />

      <BatchActionDialog
        batch={disbursing}
        title="Disburse this batch?"
        description={
          disbursing?.rowsArchived === true
            ? ARCHIVED_DISBURSE_BLOCKED
            : 'Accounts records the payment against this batch. The DCRs in it stay as they are.'
        }
        confirmLabel="Disburse"
        commentRequired
        isPending={disburse.isPending}
        onConfirm={(comments) => {
          if (disbursing !== null) {
            disburse.mutate({ batchId: disbursing.batchId, comments });
          }
        }}
        onOpenChange={(open) => {
          if (!open) setDisbursing(null);
        }}
      />
    </BatchPanelPage>
  );
}
